/**
 * Google Analytics 4 (GA4) integration module
 *
 * Setup: Add REACT_APP_GA_MEASUREMENT_ID to your .env file
 * Format: G-XXXXXXXXXX
 */

let initialized = false;

/**
 * Get the GA4 Measurement ID from environment variables
 */
export const getGaMeasurementId = () => {
  return process.env.REACT_APP_GA_MEASUREMENT_ID || "G-FYZSW8PKDZ";
};

/**
 * Initialize Google Analytics
 * This should be called once when the app loads
 */
export const initGA = () => {
  if (initialized || typeof window === "undefined") {
    return;
  }

  const measurementId = getGaMeasurementId();

  if (!measurementId) {
    // eslint-disable-next-line no-console
    console.warn(
      "Google Analytics Measurement ID not configured; skipping initialization."
    );
    return;
  }

  // Load the gtag.js script dynamically
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  // Initialize the dataLayer and gtag function
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };

  window.gtag("js", new Date());
  window.gtag("config", measurementId);

  initialized = true;
};

/**
 * Track a custom event in Google Analytics
 * @param {string} eventName - The name of the event (e.g., 'organization_signup')
 * @param {object} parameters - Optional event parameters
 */
export const trackEvent = (eventName, parameters = {}) => {
  if (!initialized || typeof window === "undefined" || !window.gtag) {
    return;
  }

  window.gtag("event", eventName, parameters);
};

/**
 * Track when a new organization is signed up
 * @param {object} options - Event options
 * @param {string} options.organizationId - The ID of the new organization
 * @param {string} options.organizationName - The name of the organization
 * @param {string} options.userId - The ID of the user who created it
 */
export const trackOrganizationSignup = ({
  organizationId,
  organizationName,
  userId,
} = {}) => {
  trackEvent("organization_signup", {
    organization_id: organizationId,
    organization_name: organizationName,
    user_id: userId,
  });
};

/**
 * Set the user ID for Google Analytics (for cross-device tracking)
 * @param {string} userId - The user's unique identifier
 */
export const setGAUser = (userId) => {
  if (!initialized || typeof window === "undefined" || !window.gtag) {
    return;
  }

  const measurementId = getGaMeasurementId();
  if (measurementId) {
    window.gtag("config", measurementId, {
      user_id: userId,
    });
  }
};

/**
 * Clear the user ID (on logout)
 */
export const clearGAUser = () => {
  if (!initialized || typeof window === "undefined" || !window.gtag) {
    return;
  }

  const measurementId = getGaMeasurementId();
  if (measurementId) {
    window.gtag("config", measurementId, {
      user_id: undefined,
    });
  }
};

export default initGA;
